import {
  createApiResponseIndividualSchema,
  createApiResponseSchema,
} from "@/types/common/pagination";
import { z } from "zod";

const userSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.email(),
  last_name: z.string().nullable(),
  dui: z.string(),
  birthdate: z.string().nullable(),
  keycloak_id: z.string(),
  deleted_at: z.string().nullable(),
  email_verified_at: z.string().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  phone_number: z.string().nullable(),
  registered_position_id: z.string().nullable(),
  department_id: z.string().nullable(),
  assigned_institution_id: z.string().nullable(),
});

const flowSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  type: z.string().nullable(),
  status: z.string().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

const tagSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  active: z.boolean(),
  image_url: z.string().optional(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  files: z
    .array(
      z.object({
        id: z.string(),
        url: z.string(),
        name: z.string(),
        size: z.number(),
      }),
    )
    .optional(),
});

export const fileSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  url: z.url(),
  file_name: z.string(),
  collection: z.string(),
  size: z.number(),
  model_id: z.uuid(),
  model_type: z.string(),
  created_by: z.uuid(),
  updated_at: z.iso.datetime(),
  created_at: z.iso.datetime(),
});

export const processSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(["PUBLISHED", "INACTIVE", "ON_CONSTRUCTION"]),
  description: z.string().optional(),
  portal_description: z.string().optional(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  createdBy: userSchema,
  flows: flowSchema,
  approved: z.boolean(),
  can_be_publish: z.boolean().optional(),
  approved_at: z.iso.datetime(),
  tags: z.array(tagSchema),
  files: z.array(fileSchema).optional(),
  estimated_cost_min: z.number().optional(),
  estimated_cost_max: z.number().optional(),
  estimated_time_min: z.number().optional(),
  estimated_time_max: z.number().optional(),
  estimated_time_unit: z.string().optional(),
  estimated_steps_min: z.number().optional(),
  estimated_steps_max: z.number().optional(),
  required_documents: z
    .array(
      z.object({
        name: z.string(),
        description: z.string().optional(),
      }),
    )
    .optional(),
});
export const ApiProcessResponseSchema = createApiResponseSchema(processSchema);

export const ApiProcessIndividualResponseSchema =
  createApiResponseIndividualSchema(processSchema);

export const newProcessSchema = z.object({
  name: z
    .string()
    .trim()
    .nonempty("El nombre del proceso es requerido")
    .min(8, "Mínimo 8 caracteres")
    .max(100, "Máximo 100 caracteres"),
  tag_id: z.string().trim().nonempty("La etiqueta es requerida"),
  description: z
    .string()
    .trim()
    .min(1, "La descripción del proceso es requerida")
    .max(500, "Máximo 500 caracteres"),
  files: z.array(z.instanceof(File)).optional(),
});

export const newParentFlowSchema = z.object({
  name: z
    .string()
    .trim()
    .nonempty("El nombre del flujo es requerido")
    .min(8, "Mínimo 8 caracteres")
    .max(100, "Máximo 100 caracteres"),
  extension_count: z
    .number({
      error: (issue) =>
        issue.input === undefined
          ? "La cantidad de prórrogas es requerida"
          : "La cantidad de prórrogas debe ser un número",
    })
    .min(0, "Mínimo 0 prórrogas")
    .max(10, "Máximo 10 prórrogas"),
});

export const newSubFlowSchema = z.object({
  name: z
    .string()
    .trim()
    .nonempty("El nombre del flujo es requerido")
    .min(8, "Mínimo 8 caracteres")
    .max(100, "Máximo 100 caracteres"),
  parent_flow_id: z.string().trim().nonempty("El flujo padre es requerido"),
});
